import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Typography, Grid, Button, Chip, Divider, TextField } from '@mui/material';
import { Filter, X } from 'lucide-react';
import CustomSelect from "../../reusableComponents/Select/CustomSelect";
import {FILE_STATUS_OPTIONS} from "../../utlis/constant";
import Slider from '@mui/material/Slider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { SUPER_ADMIN } from 'constants/constants';

const customTheme = createTheme({
  palette: {
    // Add a custom color if needed, as shown in the MUI palette customization guide
    neutral: {
        main: '#6250e4',
    },
  },
  components: {
    MuiSlider: {
      styleOverrides: {
        track: {
          backgroundColor: '#c8c6f0', // Use your custom color here
        },
        thumb: {
            backgroundColor: '#3e2aeb',
        }
      },
    },
  },
});

const FileFiltersCard = ({
                             nameFilter,
                             statusFilter,
                             notesFilter,
                             tablesFilter,
                             setNameFilter,
                             setNotesFilter,
                             setStatusFilter,
                             setPage,
                             setTablesFilter,
                             role,
                             qualityFilter,
                             onQualityFilterChange,
                             setQualityFilter
                         }) => {
    const statusOptions = FILE_STATUS_OPTIONS;
    const qualityOptions = [
        { value: "all", label: "All" },
        { value: "unreliable", label: "Unreliable" },
        { value: "poor", label: "Poor" },
        { value: "acceptable", label: "Acceptable" },
        { value: "good", label: "Good" },
        { value: "perfect", label: "Perfect" },
        { value: "not_evaluated", label: "Not evaluated" },
    ];
    const hasActiveFilters = nameFilter !== "" || statusFilter !== "all" || qualityFilter !== "all";

    const handleNameFilterClear = () => handleNameFilterChange("");
    const handleNotesFilterClear = () => handleNotesFilterChange("");
    const handleStatusFilterClear = () => handleStatusFilterChange("all");

    const handleClearFilters = () => {
        setNameFilter("");
        setNotesFilter("");
        setStatusFilter("all");
        setPage(1);
        setQualityFilter("all")
    };

    const handleNameFilterChange = (newName) => {
        setNameFilter(newName);
    };

    const handleNotesFilterChange = (newNote) => {
        setNotesFilter(newNote);
    };

    const handleTablesFilterChange = (event, newValue) => {
        setTablesFilter(newValue);
    };

    const handleStatusFilterChange = (newStatus) => {
        setStatusFilter(newStatus);
    };

    return (
        <Card sx={{ mb: 3, boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)", overflow: 'visible', position: 'relative' }}>
            <CardContent sx={{ p: 3 }}>
                {/* Filter Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Filter size={16} color="#6b7280" />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                        Filters
                    </Typography>
                    {hasActiveFilters && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearFilters}
                            sx={{
                                marginLeft: "auto",
                                color: "#6b7280",
                                textTransform: "none",
                                fontSize: "0.875rem",
                                borderColor: "#6b7280",
                                minWidth: "auto",
                                padding: "4px 8px",
                                "&:hover": {
                                    color: "#374151",
                                    backgroundColor: "transparent",
                                },
                            }}
                        >
                            <X size={16} style={{ marginRight: 4 }} />
                            Clear All
                        </Button>
                    )}
                </Box>

                <Grid container spacing={2}>
                    {/* File Name Filter */}
                    <Grid item xs={8} md={3}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                File Name
                            </Typography>

                            <TextField
                                fullWidth
                                value={nameFilter}
                                onChange={(e) => handleNameFilterChange(e.target.value)}
                                placeholder="Search by file name"
                                size="medium"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                        "& fieldset": {
                                            borderColor: "#d1d5db",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#9ca3af",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#3b82f6",
                                            borderWidth: "2px",
                                        },
                                        "& input": {
                                            padding: "8px 12px",
                                            fontSize: "0.875rem",
                                            "&::placeholder": {
                                                color: "#9ca3af",
                                                opacity: 1,
                                            },
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Status Filter */}
                    <Grid item xs={8} md={3}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                Status
                            </Typography>

                            <CustomSelect
                                fullWidth
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                name="status"
                                options={statusOptions?.map((status) => ({
                                    value: status.id,
                                    label: status.name
                                }))}
                                dropdown={'bottom'}
                            />
                        </Box>
                    </Grid>
                    {/* Notes Filter */}
                    {role === SUPER_ADMIN && <Grid item xs={8} md={3}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                File Type
                            </Typography>

                            <TextField
                                fullWidth
                                value={notesFilter}
                                onChange={(e) => handleNotesFilterChange(e.target.value)}
                                placeholder="Search by file type"
                                size="medium"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                        "& fieldset": {
                                            borderColor: "#d1d5db",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#9ca3af",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#3b82f6",
                                            borderWidth: "2px",
                                        },
                                        "& input": {
                                            padding: "8px 12px",
                                            fontSize: "0.875rem",
                                            "&::placeholder": {
                                                color: "#9ca3af",
                                                opacity: 1,
                                            },
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    }

                    {role === SUPER_ADMIN && <Grid item xs={8} md={3}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                Quality Level
                            </Typography>

                            <CustomSelect
                                fullWidth
                                value={qualityFilter}
                                onChange={onQualityFilterChange}
                                name="quality"
                                options={qualityOptions}
                                dropdown={'bottom'}
                            />
                        </Box>
                    </Grid>
                    }
                </Grid>
                    
                    {role === SUPER_ADMIN && <Grid item xs={8} md={4}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                Tables (%)
                            </Typography>

                            <ThemeProvider theme={customTheme}>
                                <Box sx={{ width: 300 }}>
                                    <Slider
                                        getAriaLabel={() => 'Tables'}
                                        value={tablesFilter}
                                        onChange={handleTablesFilterChange}
                                        valueLabelDisplay="auto"
                                        step={2}
                                    />
                                </Box>
                            </ThemeProvider>
                        </Box>
                    </Grid>
                    }
                {/* Active Filters Display */}
                {hasActiveFilters && (
                    <Box sx={{ mt: 2, pt: 2 }}>
                        <Divider sx={{ mb: 2, borderColor: "#e5e7eb" }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
                                Active Filters:
                            </Typography>
                            {nameFilter && (
                                <Chip
                                    label={`Name: ${nameFilter}`}
                                    size="small"
                                    variant="outlined"
                                    onDelete={handleNameFilterClear}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        height: 24,
                                        "& .MuiChip-label": {
                                            padding: "0 8px",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            marginRight: "3px",
                                            "&:hover": {
                                                color: "#374151",
                                            },
                                        },
                                    }}
                                />
                            )}
                            {statusFilter !== "all" && (
                                <Chip
                                    label={`Status: ${statusOptions.find(s => s.id === statusFilter)?.name || statusFilter}`}
                                    size="small"
                                    variant="outlined"
                                    onDelete={handleStatusFilterClear}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        height: 24,
                                        "& .MuiChip-label": {
                                            padding: "0 8px",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            "&:hover": {
                                                color: "#374151",
                                            },
                                        },
                                    }}
                                />
                            )}
                            {notesFilter && (
                                <Chip
                                    label={`Notes: ${notesFilter}`}
                                    size="small"
                                    variant="outlined"
                                    onDelete={handleNotesFilterClear}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        height: 24,
                                        "& .MuiChip-label": {
                                            padding: "0 8px",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            marginRight: "3px",
                                            "&:hover": {
                                                color: "#374151",
                                            },
                                        },
                                    }}
                                />
                            )}
                            {qualityFilter !== "all" && (
                                <Chip
                                    label={`Quality: ${qualityOptions.find(q => q.value === qualityFilter)?.label}`}
                                    size="small"
                                    variant="outlined"
                                    onDelete={() => onQualityFilterChange("all")}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        height: 24,
                                        "& .MuiChip-label": {
                                            padding: "0 8px",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            marginRight: "3px",
                                            "&:hover": {
                                                color: "#374151",
                                            },
                                        },
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

FileFiltersCard.propTypes = {
    nameFilter: PropTypes.string.isRequired,
    statusFilter: PropTypes.string.isRequired,
    notesFilter: PropTypes.string.isRequired,
    tablesFilter: PropTypes.array.isRequired,
    setNameFilter: PropTypes.func,
    setNotesFilter: PropTypes.func,
    setStatusFilter: PropTypes.func,
    setPage: PropTypes.func,
    setTablesFilter: PropTypes.func,
    role: PropTypes.string,
    qualityFilter:PropTypes.string.isRequired,
    onQualityFilterChange: PropTypes.func.isRequired,
    setQualityFilter: PropTypes.func.isRequired,
};

export default FileFiltersCard;